"use client";

import Link from "next/link";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";

/**
 * Custom component overrides that mirror the styles used by `LegalPortableText`
 * so switching between Portable Text and Markdown is visually seamless.
 */
const components: Components = {
  p: ({ children }) => (
    <p className="leading-7 text-foreground/90">{children}</p>
  ),
  h2: ({ children }) => (
    <h2 className="mt-8 scroll-mt-24 text-2xl font-semibold tracking-tight text-foreground">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-6 scroll-mt-24 text-xl font-semibold tracking-tight text-foreground">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="mt-4 scroll-mt-24 text-lg font-semibold tracking-tight text-foreground">
      {children}
    </h4>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-border pl-4 text-muted-foreground">
      {children}
    </blockquote>
  ),
  ul: ({ children }) => (
    <ul className="ml-6 list-disc space-y-2">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="ml-6 list-decimal space-y-2">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-7 text-foreground/90">{children}</li>
  ),
  a: ({ href, children }) => {
    const target = href ?? "#";
    const isRelative = target.startsWith("/");

    if (isRelative) {
      return (
        <Link href={target} className="underline underline-offset-4">
          {children}
        </Link>
      );
    }

    return (
      <a
        href={target}
        className="underline underline-offset-4"
        target="_blank"
        rel="noreferrer"
      >
        {children}
      </a>
    );
  },
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-border bg-muted px-4 py-2 text-left font-semibold text-foreground">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-border px-4 py-2 text-foreground/90">
      {children}
    </td>
  ),
  hr: () => <hr className="my-8 border-border" />,
  strong: ({ children }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
};

/**
 * Renders a markdown string with styled components that match the legal page
 * design system. Supports GFM (tables, strikethrough, autolinks, task lists).
 */
export function LegalMarkdown({
  content,
}: Readonly<{ content: string }>): React.ReactElement {
  return (
    <div className="space-y-4">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
